import moment from 'moment'
import React from 'react'
import { useQueryClient } from 'react-query'
import { UserPermissionsEnum } from 'src/models/user'
import {
  IPrescriptionVerificationTask,
  PrescriptionVerificationStatuses
} from 'src/models/verificationTask'
import { usePermissions } from 'src/shared/hooks/usePermissions'
import { useUpdatePrescriptionVerification } from 'src/shared/hooks/verifications'
import { ButtonWithMargin, ButtonWithWidth } from 'src/shared/styled/Buttons'
import { COLORS } from 'src/shared/theme'
import ImageDisplay from 'src/shared/views/ImageDisplay'
import ThemedTextField from 'src/shared/views/TextField'
import styled from 'styled-components'
import { formatDistanceToNow } from 'date-fns'

const PrescriptionContainer = styled.div`
  margin: 15px 0;
  background-color: #e2e8f0;
  border: 1px solid #94a3b8;
  border-radius: 5px;
  display: flex;
  flex-direction: row;
  max-height: 35rem;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.5);
`

const PrescriptionContent = styled.div`
  padding: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const PrescriptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`

const PrescriptionFooter = styled(PrescriptionRow)`
  flex-direction: column;
  margin-top: 20px;
`

const PrescriptionFooterButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`

interface IPrescriptionVerification {
  prescription: IPrescriptionVerificationTask
  userId: string
  isRejected: boolean
  updatePrescriptionState: (prescription: IPrescriptionVerificationTask) => void
}

const PrescriptionVerification = ({
  prescription,
  userId,
  updatePrescriptionState
}: IPrescriptionVerification) => {
  /**
   * ----- Hook Initialization -----
   */

  const queryClient = useQueryClient()

  const [newCustomRejectionMsg, setNewCustomRejectionMsg] = React.useState(
    prescription.verification.customRejectionMsg ||
      "The medication shown in the submitted image does not match the prescription you've selected."
  )

  const permissions = usePermissions()

  const { isLoading, updatePrescriptionVerification } =
    useUpdatePrescriptionVerification(userId, {
      onSuccess: (data) => {
        queryClient.invalidateQueries('/verification-tasks')

        updatePrescriptionState(data)
      }
    })

  /**
   * ----- Functions -----
   */

  const handleStatusChange = React.useCallback(
    (status: PrescriptionVerificationStatuses) => {
      updatePrescriptionVerification({
        prescriptionId: prescription.prescriptionId,
        status,
        customRejectionMsg:
          status === PrescriptionVerificationStatuses.Rejected
            ? newCustomRejectionMsg
            : undefined
      })
    },
    [
      newCustomRejectionMsg,
      prescription.prescriptionId,
      updatePrescriptionVerification
    ]
  )

  /**
   * ----- Variables -----
   */

  const loading = isLoading

  const rejectDisabled =
    prescription.verification.status ===
      PrescriptionVerificationStatuses.Rejected || !newCustomRejectionMsg

  /**
   * ----- Render -----
   */

  return (
    <PrescriptionContainer>
      {prescription.verification.imageUrl && (
        <ImageDisplay
          imageKey={prescription.verification.imageUrl}
          style={{ borderRadius: '5px 0 0 5px', width: '40%' }}
        />
      )}
      <PrescriptionContent>
        <PrescriptionRow>
          <div>
            <h2>
              {prescription.drugName} - {prescription.din}
            </h2>
            <h3>{prescription.doseStrength}</h3>
            <h4>
              Submitted on:{' '}
              {moment(prescription.verification.dateSubmitted).format(
                'MMMM DD YYYY hh:mma'
              )}
            </h4>
            {prescription.verification.dateFilled && (
              <h4>
                Filled date:{' '}
                {moment(prescription.verification.dateFilled).format(
                  'MMMM DD YYYY hh:mma'
                )}{' '}
                (
                {formatDistanceToNow(
                  new Date(prescription.verification.dateFilled),
                  { addSuffix: true }
                )}
                )
              </h4>
            )}
            <h3>{prescription.verification.status}</h3>
            {prescription.verification.customRejectionMsg && (
              <p>
                <b>Rejection Message: </b>
                {prescription.verification.customRejectionMsg}
              </p>
            )}
          </div>
        </PrescriptionRow>
        <PrescriptionFooter>
          <ThemedTextField
            label="Custom Rejection Message *"
            disabled={loading}
            variant="outlined"
            multiline
            minRows={2}
            fullWidth
            style={{
              marginTop: 10
            }}
            onChange={(e) => setNewCustomRejectionMsg(e.target.value)}
            value={newCustomRejectionMsg}
          />
          <PrescriptionFooterButtons>
            {prescription.verification.status ===
              PrescriptionVerificationStatuses.Rejected &&
            newCustomRejectionMsg !==
              prescription.verification.customRejectionMsg ? (
              <ButtonWithWidth
                color="secondary"
                isLoading={loading}
                onClick={() =>
                  handleStatusChange(PrescriptionVerificationStatuses.Rejected)
                }
                tooltip={'Update rejection message'}
              >
                Update Message
              </ButtonWithWidth>
            ) : (
              <div></div>
            )}
            <div>
              {permissions.includes(UserPermissionsEnum.PLATFORM_ADMIN) && (
                <ButtonWithMargin
                  color="secondary"
                  isLoading={loading}
                  onClick={() =>
                    handleStatusChange(PrescriptionVerificationStatuses.Expired)
                  }
                >
                  Expired
                </ButtonWithMargin>
              )}
              <ButtonWithMargin
                style={{
                  backgroundColor: !rejectDisabled
                    ? COLORS.warningRed
                    : undefined
                }}
                color="secondary"
                isLoading={loading}
                onClick={() =>
                  handleStatusChange(PrescriptionVerificationStatuses.Rejected)
                }
                disabled={rejectDisabled}
                tooltip={
                  rejectDisabled ? 'Please provide a rejection message' : ''
                }
              >
                Reject
              </ButtonWithMargin>
              <ButtonWithMargin
                isLoading={loading}
                disabled={
                  prescription.verification.status ===
                  PrescriptionVerificationStatuses.Verified
                }
                onClick={() =>
                  handleStatusChange(PrescriptionVerificationStatuses.Verified)
                }
              >
                Verify
              </ButtonWithMargin>
            </div>
          </PrescriptionFooterButtons>
        </PrescriptionFooter>
      </PrescriptionContent>
    </PrescriptionContainer>
  )
}

export default PrescriptionVerification
